/**
 * VehicleFormDialog - Modal de cadastro/edição de veículo
 *
 * Ordem dos campos (padrão do requisito):
 *   Placa | Tipo de Veículo · Marca | Ano · Modelo | Cor · Renavam | Chassi
 *   Código FIPE | Valor (R$) · Observações
 *
 * Tipos CAR/MOTORCYCLE/TRUCK usam a base global FIPE (marca > modelo > ano),
 * com preenchimento automático de código FIPE e valor. O tipo "Outros" usa
 * as categorias criadas pelo próprio tenant.
 *
 * @version 2.8.0
 * RELEASE: 2026-10-04 v2.8.0 - Cliente/Contato passa a ser obrigatório no cadastro avulso
 *          (alinhado à regra do banco customer_vehicles_owner_chk): rótulo com *,
 *          aviso e destaque ao salvar sem proprietário.
 * RELEASE: 2026-10-02 v2.7.0 - Status na mesma linha do Cliente/Contato
 *          (3/5 cliente · 2/5 status), fora da seção própria; sem o campo de
 *          cliente o status ocupa a linha inteira.
 * RELEASE: 2026-10-02 v2.6.0 - Campo Status do veículo (catálogo sistema + personalizados).
 * RELEASE: 2026-09-29 v2.5.0 - Foto de perfil do veículo no cabeçalho (avatar circular). Edição envia
 *          na hora; no cadastro a foto fica em espera e é enviada após o veículo receber o id
 *          (falha no envio não desfaz o cadastro).
 * RELEASE: 2026-08-09 v2.4.0 - Seção "Vínculos" (negócios/tarefas) em modo edição,
 *          permitindo visualizar e desvincular sem sair do formulário
 * RELEASE: 2026-08-08 v1.0.0 - Criação inicial
 * RELEASE: 2026-08-08 v2.0.0 - Consulta por placa (auto-preenchimento) via plate-proxy
 * RELEASE: 2026-08-08 v2.3.0 - Marca na primeira linha (Placa · Tipo · Marca);
 *          modal de confirmação quando a placa retorna várias correspondências FIPE;
 *          máscara BRL e bloqueio de edição em Código FIPE e Valor.
 */


import { VehicleStatusSelect } from '@/components/vehicles/VehicleStatus';
import { useEffect, useMemo, useState } from 'react';
import {
  Calculator,
  Calendar,
  Car,
  Check,
  ChevronsUpDown,
  FileText,
  Layers,
  Loader2,
  ScanSearch,
  Sparkles,
  StickyNote,
  User,
} from 'lucide-react';

import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { FormSection, FieldLabel } from '@/components/common/FormSection';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
// v2.0.0 — consulta por placa (Edge Function plate-proxy)
import {
  usePlateLookup,
  isValidPlate,
  type PlateVehicleData,
  type PlateFipeCandidate,
} from '@/hooks/usePlateLookup';
// v2.3.0 — modal de confirmação da correspondência FIPE
import { PlateFipeMatchDialog } from '@/components/vehicles/PlateFipeMatchDialog';
// v2.4.0 — seção de vínculos (negócios/tarefas) exibida na edição
import { VehicleLinksSection } from '@/components/vehicles/VehicleLinksSection';

import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { VehiclePhotoField, uploadVehiclePhoto } from '@/components/vehicles/VehiclePhotoField';
import { ContactSelector } from '@/components/crm/ContactSelector';
import { useVehiclesModule } from '@/hooks/useVehiclesModule';
import { usePermissionsContext } from '@/contexts/PermissionsContext';

import {
  useCustomerVehicles,
  VEHICLE_TYPE_LABELS,
  type CustomerVehicle,
  type VehicleScope,
  type VehicleType,
} from '@/hooks/useCustomerVehicles';
import {
  useFipeBrands,
  useFipeModels,
  useFipePrice,
  useFipeResolve,
  useFipeYears,
  type FipeVehicleType,
} from '@/hooks/useFipeLookup';

/**
 * v2.3.0 — Formata um valor numérico (string ou number) no padrão brasileiro
 * de moeda. Usado apenas para exibição; o valor salvo continua numérico puro.
 */
function formatCurrencyBR(value: string | number | null | undefined): string {
  if (value === null || value === undefined || value === '') return '';
  const numeric = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(numeric)) return '';
  return numeric.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  });
}


/** Props do modal */
interface VehicleFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Cliente/contato dono do veículo */
  scope: VehicleScope;
  /** Veículo em edição (null = novo) */
  vehicle?: CustomerVehicle | null;
  /**
   * v1.1.0 — Quando true, exibe o seletor opcional de contato/cliente dono
   * do veículo (usado na página "Lista Veículos", com cadastro avulso).
   */
  allowOwnerSelection?: boolean;
  /** Callback após salvar com sucesso */
  onSaved?: (vehicleId: string) => void;
}


/** Estado interno do formulário */
interface FormState {
  plate: string;
  vehicle_type: VehicleType;
  category_id: string | null;
  brand_id: string | null;
  model_id: string | null;
  year_code: string | null;
  color: string;
  renavam: string;
  chassis: string;
  fipe_code: string;
  fipe_value: string;
  notes: string;
  /** 2026-10-02: status; nulo = manter o atual (veículo sem status carregado) */
  status_id: string | null;
}

/** Estado inicial vazio */
const EMPTY: FormState = {
  plate: '',
  vehicle_type: 'CAR',
  category_id: null,
  brand_id: null,
  model_id: null,
  year_code: null,
  color: '',
  renavam: '',
  chassis: '',
  fipe_code: '',
  fipe_value: '',
  notes: '',
  status_id: 'active',
};

/** Combobox simples com busca (marca/modelo) */
function SearchableSelect({
  value,
  options,
  placeholder,
  emptyText,
  disabled,
  loading,
  onChange,
}: {
  value: string | null;
  options: Array<{ value: string; label: string }>;
  placeholder: string;
  emptyText: string;
  disabled?: boolean;
  loading?: boolean;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          className="w-full justify-between font-normal"
        >
          <span className={cn('truncate', !selected && 'text-muted-foreground')}>
            {loading ? 'Carregando...' : selected?.label || placeholder}
          </span>
          {loading ? (
            <Loader2 className="ml-2 h-4 w-4 shrink-0 animate-spin opacity-50" />
          ) : (
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar..." />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={option.label}
                  onSelect={() => {
                    onChange(option.value);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === option.value ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="truncate">{option.label}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/** Modal de cadastro/edição de veículo */
export function VehicleFormDialog({
  open,
  onOpenChange,
  scope,
  vehicle = null,
  allowOwnerSelection = false,
  onSaved,
}: VehicleFormDialogProps) {
  const isMobile = useIsMobile();
  const { activeCategories, plateLookupEnabled: plateLookupFlag } = useVehiclesModule();
  const { canView, isAdmin } = usePermissionsContext();
  // Fase 5: além da flag do tenant, exige permissão de consulta por placa
  const plateLookupEnabled = plateLookupFlag && (isAdmin || canView('vehicles_plate_lookup'));
  const { saveVehicle } = useCustomerVehicles(scope, false);
  const [form, setForm] = useState<FormState>(EMPTY);
  // v1.1.0 — Contato dono do veículo quando o cadastro é avulso
  const [ownerContactId, setOwnerContactId] = useState<string | null>(null);
  // v2.0.0 — Resultado da consulta por placa (usado como fallback dos dados FIPE)
  const [plateData, setPlateData] = useState<PlateVehicleData | null>(null);
  const plateLookup = usePlateLookup();
  // v2.3.0 — Correspondências FIPE devolvidas pela placa e modal de confirmação
  const [fipeCandidates, setFipeCandidates] = useState<PlateFipeCandidate[]>([]);
  const [matchDialogOpen, setMatchDialogOpen] = useState(false);
  const fipeResolve = useFipeResolve();
  // v2.5.0 — tenant ativo e foto em espera (somente no cadastro)
  const { currentTenant } = useAuth();
  const photoTenantId = currentTenant?.id ?? '';
  const [pendingPhoto, setPendingPhoto] = useState<File | null>(null);
  const [photoSaving, setPhotoSaving] = useState(false);


  // Carrega valores ao abrir (edição) ou limpa (novo)
  useEffect(() => {
    if (!open) return;
    setPendingPhoto(null); // v2.5.0 — descarta foto em espera de aberturas anteriores
    setPlateData(null); // v2.0.0 — limpa consulta anterior ao reabrir
    setFipeCandidates([]); // v2.3.0 — limpa correspondências FIPE anteriores
    setOwnerContactId(vehicle?.contact_id ?? scope.contactId ?? null);

    setForm(
      vehicle
        ? {
            plate: vehicle.plate ?? '',
            vehicle_type: vehicle.vehicle_type,
            category_id: vehicle.category_id,
            brand_id: vehicle.brand_id,
            model_id: vehicle.model_id,
            year_code: vehicle.year_code,
            color: vehicle.color ?? '',
            renavam: vehicle.renavam ?? '',
            chassis: vehicle.chassis ?? '',
            fipe_code: vehicle.fipe_code ?? '',
            fipe_value: vehicle.fipe_value != null ? String(vehicle.fipe_value) : '',
            notes: vehicle.notes ?? '',
            status_id: vehicle.status_id ?? null,
          }
        : EMPTY,
    );
  }, [open, vehicle, scope.contactId]);

  // v2.7.0: exibir o seletor de Cliente/Contato (somente cadastro avulso, sem vínculo definido)
  const showOwnerField = allowOwnerSelection && !scope.customerId && !scope.contactId;
  // v2.8.0: destaque do campo proprietário quando ausente ao salvar
  const [ownerError, setOwnerError] = useState(false);


  const isFipeType = form.vehicle_type !== 'OTHER';
  const fipeType = (isFipeType ? form.vehicle_type : null) as FipeVehicleType | null;

  const brandsQuery = useFipeBrands(fipeType);
  const modelsQuery = useFipeModels(fipeType, form.brand_id);
  const yearsQuery = useFipeYears(fipeType, form.model_id);
  const priceQuery = useFipePrice(fipeType, form.model_id, form.year_code);

  // Autopreenche código FIPE e valor quando o preço é resolvido
  useEffect(() => {
    if (!priceQuery.data) return;
    setForm((prev) => ({
      ...prev,
      fipe_code: priceQuery.data?.fipe_code ?? prev.fipe_code,
      fipe_value:
        priceQuery.data?.valor != null ? String(priceQuery.data.valor) : prev.fipe_value,
    }));
  }, [priceQuery.data]);

  const brandOptions = useMemo(
    () => (brandsQuery.data ?? []).map((b) => ({ value: b.id, label: b.nome_marca ?? '' })),
    [brandsQuery.data],
  );
  const modelOptions = useMemo(
    () => (modelsQuery.data ?? []).map((m) => ({ value: m.id, label: m.nome_modelo ?? '' })),
    [modelsQuery.data],
  );

  /** Atualiza um campo do formulário */
  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  /**
   * v2.1.0 — Consulta a placa e preenche automaticamente os campos.
   * Marca/modelo da consulta são usados apenas como fallback quando o usuário
   * não seleciona a combinação correspondente na base FIPE.
   * O erro é tratado aqui (o hook já exibe o toast) para não gerar rejeição
   * não tratada, que derrubava a tela no error boundary.
   */
  const handlePlateLookup = async () => {
    try {
      const result = await plateLookup.mutateAsync(form.plate);
      setPlateData(result);

      // v2.2.0 — IDs resolvidos na base global FIPE (unificação dos provedores):
      // o provedor de placa devolve texto; o `fipe_match` traz os IDs usados
      // pelos selects de Marca/Modelo/Ano.
      const match = result.fipe_match?.matched ? result.fipe_match : null;

      setForm((prev) => ({
        ...prev,
        vehicle_type: result.vehicle_type ?? prev.vehicle_type,
        color: result.color || prev.color,
        renavam: result.renavam || prev.renavam,
        chassis: result.chassis || prev.chassis,
        // Preenchimento automático dos selects quando houve resolução
        brand_id: match?.brand_id || prev.brand_id,
        model_id: match?.model_id || prev.model_id,
        year_code: match?.year_code || prev.year_code,
        // v2.1.0 — dados FIPE devolvidos pelo provedor (quando disponíveis)
        fipe_code: match?.fipe_code || result.fipe_code || prev.fipe_code,
        fipe_value:
          match?.valor != null
            ? String(match.valor)
            : result.fipe_value != null
              ? String(result.fipe_value)
              : prev.fipe_value,
      }));

      // v2.3.0 — Mais de uma correspondência FIPE: pede confirmação ao usuário
      const candidates = result.fipe_candidates ?? [];
      setFipeCandidates(candidates);
      if (candidates.length > 1) setMatchDialogOpen(true);
    } catch {
      /* mensagem já exibida pelo hook usePlateLookup */
    }
  };

  /**
   * v2.3.0 — Aplica a correspondência FIPE escolhida no modal, resolvendo os
   * IDs da base global (Marca/Modelo/Ano) via ação `resolve` do fipe-proxy.
   */
  const handleApplyCandidate = async (candidate: PlateFipeCandidate) => {
    const type = (form.vehicle_type !== 'OTHER'
      ? form.vehicle_type
      : 'CAR') as FipeVehicleType;
    try {
      const resolved = await fipeResolve.mutateAsync({
        vehicle_type: type,
        brand_text: candidate.brand_text,
        model_text: candidate.model_text,
        year: candidate.year,
        fuel: candidate.fuel,
        fipe_code: candidate.fipe_code,
      });

      setForm((prev) => ({
        ...prev,
        brand_id: resolved.matched ? (resolved.brand_id ?? prev.brand_id) : prev.brand_id,
        model_id: resolved.matched ? (resolved.model_id ?? prev.model_id) : prev.model_id,
        year_code: resolved.matched ? (resolved.year_code ?? prev.year_code) : prev.year_code,
        fipe_code: resolved.fipe_code || candidate.fipe_code || prev.fipe_code,
        fipe_value:
          resolved.valor != null
            ? String(resolved.valor)
            : candidate.value != null
              ? String(candidate.value)
              : prev.fipe_value,
      }));

      // Mantém o resumo da placa coerente com a escolha do usuário
      setPlateData((prev) =>
        prev
          ? {
              ...prev,
              fipe_match: resolved.matched
                ? { ...resolved, matched: true }
                : prev.fipe_match,
            }
          : prev,
      );
      setMatchDialogOpen(false);
    } catch {
      /* falha já logada pelo hook useFipeResolve */
    }
  };




  /** Salva o veículo via RPC */
  const handleSubmit = async () => {
    // v2.8.0: proprietário obrigatório (o servidor também valida)
    if (showOwnerField && !ownerContactId && !vehicle?.customer_id) {
      setOwnerError(true);
      toast.error('Informe o cliente ou contato proprietário do veículo');
      return;
    }
    const brandName = brandOptions.find((b) => b.value === form.brand_id)?.label ?? null;
    const modelName = modelOptions.find((m) => m.value === form.model_id)?.label ?? null;
    const year = (yearsQuery.data ?? []).find((y) => y.codigo_ano === form.year_code);

    const id = await saveVehicle.mutateAsync({
      id: vehicle?.id ?? null,
      customerId: scope.customerId ?? null,
      // v1.1.0 — cadastro avulso pode escolher o contato dono (ou nenhum)
      contactId: scope.contactId ?? ownerContactId ?? null,

      vehicle_type: form.vehicle_type,
      category_id: form.vehicle_type === 'OTHER' ? form.category_id : null,
      brand_id: isFipeType ? form.brand_id : null,
      // v2.0.0 — fallback para os nomes vindos da consulta por placa
      brand_name: brandName ?? plateData?.brand ?? null,
      model_id: isFipeType ? form.model_id : null,
      model_name: modelName ?? plateData?.model ?? null,
      year_code: isFipeType ? form.year_code : null,
      year: year?.ano ?? plateData?.year ?? null,
      fuel: year?.combustivel ?? plateData?.fuel ?? null,
      color: form.color || null,
      plate: form.plate || null,
      renavam: form.renavam || null,
      chassis: form.chassis || null,
      fipe_code: form.fipe_code || null,
      fipe_value: form.fipe_value ? Number(form.fipe_value) : null,
      notes: form.notes || null,
      status_id: form.status_id,
    });

    // v2.5.0 — envia a foto em espera após o cadastro; falha não bloqueia o veículo salvo
    if (!vehicle && pendingPhoto && id && photoTenantId) {
      setPhotoSaving(true);
      try {
        await uploadVehiclePhoto(String(id), photoTenantId, pendingPhoto);
      } catch (e) {
        toast.error(`Veículo salvo, mas a foto não foi enviada: ${(e as Error).message}. Tente pela ficha do veículo.`);
      } finally {
        setPhotoSaving(false);
      }
    }

    onSaved?.(id);
    onOpenChange(false);
  };

  // Regras mínimas de habilitação do botão salvar
  // v2.0.0 — dados vindos da placa também satisfazem a identificação mínima
  const canSubmit =
    (form.vehicle_type === 'OTHER'
      ? !!form.category_id
      : !!form.model_id || !!plateData?.model) && !saveVehicle.isPending;


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'flex flex-col gap-0 overflow-hidden p-0',
          isMobile
            ? 'h-[100dvh] w-screen max-w-full rounded-none'
            : 'max-h-[90vh] max-w-5xl',
        )}
      >
        {/* Cabeçalho premium (padrão DealFormDialog) */}
        <DialogHeader className="shrink-0 border-b p-6 pb-4">
          <div className="flex items-center gap-3">
            {/* v2.5.0 — avatar circular da foto de perfil do veículo */}
            <VehiclePhotoField
              layout="avatar"
              vehicleId={vehicle?.id ?? null}
              tenantId={photoTenantId}
              canEdit={!!photoTenantId}
              pendingFile={pendingPhoto}
              onSelectFile={setPendingPhoto}
              saving={saveVehicle.isPending || photoSaving}
            />
            <div className="text-left">
              <DialogTitle className="text-lg">
                {vehicle ? 'Editar Veículo' : 'Novo Veículo'}
              </DialogTitle>
              <DialogDescription className="text-sm">
                Dados do veículo vinculado ao cliente, usados também na geração de contratos.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 overflow-auto">
          <div className="space-y-6 p-6">
            {/* ================= Identificação ================= */}
            <FormSection icon={Car} title="Identificação">
              {/* v2.7.0 — Cliente/Contato (3/5) e Status (2/5) na mesma linha;
                  sem o campo de cliente o status ocupa a largura total */}
              <div className={cn(
                'grid grid-cols-1 gap-3',
                showOwnerField && 'sm:grid-cols-5',
              )}>
                {showOwnerField && (
                  <div className="space-y-1.5 sm:col-span-3">
                    <FieldLabel required>
                      <User className="h-3 w-3" />
                      Cliente / Contato
                    </FieldLabel>
                    <div className={cn(ownerError && !ownerContactId && 'rounded-md ring-2 ring-destructive')}>
                    <ContactSelector
                      value={ownerContactId}
                      onSelect={(v) => { setOwnerContactId(v); setOwnerError(false); }}
                      placeholder="Selecione o cliente ou contato"
                    />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Proprietário do veículo (obrigatório).
                    </p>
                  </div>
                )}
                <div className={cn('space-y-1.5', showOwnerField && 'sm:col-span-2')}>
                  <FieldLabel>Status</FieldLabel>
                  <VehicleStatusSelect
                    value={form.status_id}
                    onChange={(v) => setField('status_id', v)}
                    placeholder="Manter status atual"
                    className="h-9 border-border/50"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">

                {/* Placa (+ consulta automática quando habilitada) */}
                <div className="space-y-1.5">
                  <FieldLabel htmlFor="vehicle-plate">Placa</FieldLabel>
                  <div className="flex gap-2">
                    <Input
                      id="vehicle-plate"
                      value={form.plate}
                      maxLength={8}
                      placeholder="ABC1D23"
                      className="h-9 border-border/50 font-mono uppercase"
                      onChange={(e) => {
                        setField('plate', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''));
                        setPlateData(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && plateLookupEnabled && isValidPlate(form.plate)) {
                          e.preventDefault();
                          void handlePlateLookup();
                        }
                      }}
                    />
                    {plateLookupEnabled && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-9 shrink-0"
                        disabled={!isValidPlate(form.plate) || plateLookup.isPending}
                        onClick={() => void handlePlateLookup()}
                      >
                        {plateLookup.isPending ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <ScanSearch className="h-4 w-4" />
                        )}
                        <span className="ml-1.5 hidden sm:inline">Buscar</span>
                      </Button>
                    )}
                  </div>
                  {/* v2.1.0 — dica quando a consulta por placa está indisponível,
                      evitando a impressão de que o recurso não existe */}
                  {!plateLookupEnabled && (
                    <p className="text-xs text-muted-foreground">
                      {plateLookupFlag
                        ? 'Consulta por placa indisponível: seu perfil não tem a permissão necessária.'
                        : 'Consulta automática por placa desativada em Configurações › Veículos.'}
                    </p>
                  )}
                </div>



                {/* Tipo de veículo */}
                <div className="space-y-1.5">
                  <FieldLabel>
                    <Layers className="h-3 w-3" />
                    Tipo de Veículo
                  </FieldLabel>
                  <Select
                    value={form.vehicle_type}
                    onValueChange={(value) =>
                      setForm((prev) => ({
                        ...prev,
                        vehicle_type: value as VehicleType,
                        brand_id: null,
                        model_id: null,
                        year_code: null,
                        category_id: null,
                        fipe_code: '',
                        fipe_value: '',
                      }))
                    }
                  >
                    <SelectTrigger className="h-9 border-border/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.keys(VEHICLE_TYPE_LABELS) as VehicleType[]).map((type) => (
                        <SelectItem key={type} value={type}>
                          {VEHICLE_TYPE_LABELS[type]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* v2.3.0 — Marca (FIPE) ou Categoria (Outros) fecha a linha */}
                {isFipeType ? (
                  <div className="space-y-1.5">
                    <FieldLabel required>Marca</FieldLabel>
                    <SearchableSelect
                      value={form.brand_id}
                      options={brandOptions}
                      placeholder="Selecione a marca"
                      emptyText="Nenhuma marca encontrada"
                      loading={brandsQuery.isLoading}
                      onChange={(value) =>
                        setForm((prev) => ({
                          ...prev,
                          brand_id: value,
                          model_id: null,
                          year_code: null,
                          fipe_code: '',
                          fipe_value: '',
                        }))
                      }
                    />
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <FieldLabel required>Categoria</FieldLabel>
                    <Select
                      value={form.category_id ?? undefined}
                      onValueChange={(value) => setField('category_id', value)}
                    >
                      <SelectTrigger className="h-9 border-border/50">
                        <SelectValue placeholder="Selecione a categoria" />
                      </SelectTrigger>
                      <SelectContent>
                        {activeCategories.map((category) => (
                          <SelectItem key={category.id} value={category.id}>
                            {category.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {activeCategories.length === 0 && (
                      <p className="text-xs text-muted-foreground">
                        Cadastre categorias em Configurações › Veículos.
                      </p>
                    )}
                  </div>
                )}
              </div>


              {/* v2.0.0 — Resumo dos dados retornados pela consulta da placa */}
              {plateData && (
                <div className="rounded-lg border border-primary/30 bg-primary/5 p-3">
                  <div className="mb-2 flex items-center gap-2 text-xs font-medium text-primary">
                    <Sparkles className="h-3.5 w-3.5" />
                    Dados encontrados para a placa {plateData.plate}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {[
                      plateData.brand_model_raw ??
                        [plateData.brand, plateData.model].filter(Boolean).join(' '),
                      plateData.year ? `Ano ${plateData.year}` : null,
                      plateData.fuel,
                      plateData.color,
                      plateData.uf,
                      plateData.situation,
                    ]
                      .filter(Boolean)
                      .map((label) => (
                        <Badge key={String(label)} variant="secondary" className="font-normal">
                          {label}
                        </Badge>
                      ))}
                  </div>
                  {/* v2.2.0 — Feedback da resolução na base global FIPE */}
                  {plateData.fipe_match?.matched ? (
                    <p className="mt-2 text-xs text-muted-foreground">
                      {plateData.fipe_match.confidence === 'confirmed'
                        ? 'Marca, modelo e ano preenchidos e confirmados pelo código FIPE.'
                        : 'Marca, modelo e ano preenchidos como sugestão — confira antes de salvar.'}
                    </p>
                  ) : (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Confirme marca e modelo na base FIPE abaixo para obter o valor de referência.
                    </p>
                  )}
                  {/* v2.3.0 — Permite reabrir a lista de correspondências FIPE */}
                  {fipeCandidates.length > 1 && (
                    <Button
                      type="button"
                      variant="link"
                      className="mt-1 h-auto p-0 text-xs"
                      onClick={() => setMatchDialogOpen(true)}
                    >
                      Trocar correspondência FIPE ({fipeCandidates.length} opções)
                    </Button>
                  )}
                </div>


              )}

              {/* v2.3.0 — Marca/Categoria movidos para a primeira linha */}

            </FormSection>

            <Separator />

            {/* ================= Dados FIPE ================= */}
            <FormSection icon={Calculator} title="Dados FIPE">
              {isFipeType && (
                <div className="grid grid-cols-2 gap-3">
                  {/* Modelo */}
                  <div className="space-y-1.5">
                    <FieldLabel required>Modelo</FieldLabel>
                    <SearchableSelect
                      value={form.model_id}
                      options={modelOptions}
                      placeholder="Selecione o modelo"
                      emptyText="Nenhum modelo encontrado"
                      disabled={!form.brand_id}
                      loading={modelsQuery.isLoading}
                      onChange={(value) =>
                        setForm((prev) => ({
                          ...prev,
                          model_id: value,
                          year_code: null,
                          fipe_code: '',
                          fipe_value: '',
                        }))
                      }
                    />
                  </div>

                  {/* Ano */}
                  <div className="space-y-1.5">
                    <FieldLabel>
                      <Calendar className="h-3 w-3" />
                      Ano
                    </FieldLabel>
                    <Select
                      value={form.year_code ?? undefined}
                      disabled={!form.model_id || yearsQuery.isLoading}
                      onValueChange={(value) => setField('year_code', value)}
                    >
                      <SelectTrigger className="h-9 border-border/50">
                        <SelectValue
                          placeholder={yearsQuery.isLoading ? 'Carregando...' : 'Selecione o ano'}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {(yearsQuery.data ?? []).map((year) => (
                          <SelectItem key={year.codigo_ano} value={year.codigo_ano}>
                            {year.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* v2.3.0 — Código FIPE e Valor são somente leitura: preenchidos
                  exclusivamente pela base FIPE / consulta por placa */}
              <div className="grid grid-cols-2 gap-3">
                {/* Código FIPE */}
                <div className="space-y-1.5">
                  <FieldLabel htmlFor="vehicle-fipe-code">Código FIPE</FieldLabel>
                  <Input
                    id="vehicle-fipe-code"
                    value={form.fipe_code}
                    readOnly
                    tabIndex={-1}
                    placeholder="—"
                    className="h-9 cursor-default border-transparent bg-muted/50 font-mono text-muted-foreground focus-visible:ring-0"
                  />
                  <p className="text-xs text-muted-foreground">
                    {isFipeType && priceQuery.isFetching
                      ? 'Consultando tabela FIPE...'
                      : 'Auto-preenchido com dados FIPE'}
                  </p>
                </div>

                {/* Valor (máscara BRL, somente leitura) */}
                <div className="space-y-1.5">
                  <FieldLabel htmlFor="vehicle-value">Valor (R$)</FieldLabel>
                  <Input
                    id="vehicle-value"
                    value={formatCurrencyBR(form.fipe_value)}
                    readOnly
                    tabIndex={-1}
                    placeholder="—"
                    className="h-9 cursor-default border-transparent bg-muted/50 text-right text-muted-foreground focus-visible:ring-0"
                  />
                  <p className="text-xs text-muted-foreground">
                    {isFipeType && priceQuery.data?.mes_referencia
                      ? `Auto-preenchido com dados FIPE (${priceQuery.data.mes_referencia.trim()})`
                      : 'Auto-preenchido com dados FIPE'}
                  </p>
                </div>
              </div>

            </FormSection>

            <Separator />

            {/* ================= Detalhes ================= */}
            <FormSection icon={FileText} title="Detalhes do Veículo">
              <div className="grid grid-cols-2 gap-3">
                {/* Cor */}
                <div className="space-y-1.5">
                  <FieldLabel htmlFor="vehicle-color">Cor</FieldLabel>
                  <Input
                    id="vehicle-color"
                    value={form.color}
                    maxLength={40}
                    placeholder="Ex.: Prata"
                    className="h-9 border-border/50"
                    onChange={(e) => setField('color', e.target.value)}
                  />
                </div>

                {/* Renavam */}
                <div className="space-y-1.5">
                  <FieldLabel htmlFor="vehicle-renavam">Renavam</FieldLabel>
                  <Input
                    id="vehicle-renavam"
                    value={form.renavam}
                    maxLength={11}
                    inputMode="numeric"
                    placeholder="00000000000"
                    className="h-9 border-border/50 font-mono"
                    onChange={(e) => setField('renavam', e.target.value.replace(/\D/g, ''))}
                  />
                </div>
              </div>

              {/* Chassi */}
              <div className="space-y-1.5">
                <FieldLabel htmlFor="vehicle-chassis">Chassi (VIN)</FieldLabel>
                <Input
                  id="vehicle-chassis"
                  value={form.chassis}
                  maxLength={17}
                  placeholder="17 caracteres"
                  className="h-9 border-border/50 font-mono uppercase"
                  onChange={(e) =>
                    setField('chassis', e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))
                  }
                />
              </div>
            </FormSection>

            <Separator />

            {/* ================= Observações ================= */}
            <FormSection icon={StickyNote} title="Observações" hint="(opcional)">
              <Textarea
                id="vehicle-notes"
                value={form.notes}
                maxLength={2000}
                rows={3}
                placeholder="Informações adicionais sobre o veículo"
                className="border-border/50"
                onChange={(e) => setField('notes', e.target.value)}
              />
            </FormSection>

            {/* v2.4.0 — Vínculos (negócios/tarefas) apenas em modo edição */}
            <VehicleLinksSection vehicleId={vehicle?.id ?? null} enabled={open} />
          </div>
        </ScrollArea>


        {/* Rodapé fixo (padrão DealFormDialog) */}
        <DialogFooter className="shrink-0 gap-2 border-t p-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {saveVehicle.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* v2.3.0 — Confirmação da correspondência FIPE quando há várias opções */}
      <PlateFipeMatchDialog
        open={matchDialogOpen}
        onOpenChange={setMatchDialogOpen}
        plate={form.plate}
        candidates={fipeCandidates}
        isApplying={fipeResolve.isPending}
        onConfirm={(candidate) => void handleApplyCandidate(candidate)}
      />
    </Dialog>
  );
}

